import { DashboardState, ESdChangeReason, IScheduledJob, IScheduledJobsDto } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import * as React from "react";
import { connect } from "react-redux";
import { ScheduledJobsService } from "../../../services/scheduled-jobs-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { openScheduledJobForEditing } from "../../../shared/utility/designer-view-actions";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { DeleteModal } from "../../common/delete-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { IScheduledJobEditorFormValues, IScheduledJobEditorSnapshot, ScheduledJobEditor } from "./scheduled-job-editor";
import { buildScheduledJobRowActions } from "./scheduled-job-row-actions";
import { IScheduledJobTemplate, sortScheduledJobs } from "./scheduled-job-templates";
import { DEFAULT_TIME_ZONE } from "./scheduled-job-time-zones";
import { ScheduledJobsLanding } from "./scheduled-jobs-landing";

/**
 * Host for the Scheduled Jobs tool: it decides between the landing list and the editor,
 * and owns every mutation.
 *
 * The mutations live here rather than in either surface because this component stays
 * mounted while the editor is up, so a create that finishes after the list has unmounted
 * still has somewhere to land — the setState-after-unmount shape the Email Builder hit
 * when its duplicate lived in the list.
 */

/** What a blank job is scheduled to do until the user says otherwise: every hour. */
const DEFAULT_CRON_VALUE: string = "0 0 * ? * * *";

export interface IScheduledJobsCanvasProps {
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (showModal: boolean) => void;
}

interface IScheduledJobsCanvasState {
  /** A create or an update is in flight — the name sheet and the close dialog wait on it. */
  isLoading: boolean;
  /** Where the editor's UPDATE stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where the name sheet's create stands. Owned by `SaveStatus`, never set directly. */
  createStatus: KuikaButtonStatus;
  /** True until the first `loadScheduledJobs` settles, so the table spins instead of
      claiming the project has no jobs. */
  isListLoading: boolean;
  busyJobId?: string;
  jobToDelete?: IScheduledJob;
}

class ScheduledJobsCanvas extends PureSdComponent<IScheduledJobsCanvasProps & IWithSDProps, IScheduledJobsCanvasState> {
  /** The editor's current form state, and the baseline the dirty check compares it to. */
  private readEditor?: () => IScheduledJobEditorSnapshot;

  private baseline?: IScheduledJobEditorSnapshot;

  /** True while an "Update & close" is running, so the save knows to close afterwards. */
  private pendingClose: boolean = false;

  /**
   * True from a successful "Update & close" until its ✓ has been held and the editor closed. The
   * machine goes back to idle a moment before it hands over, and that re-render must not close
   * the editor a second time through the navigation check in `componentDidUpdate`.
   */
  private isClosingAfterSave: boolean = false;

  /** The editor's form is submitted from here when the close dialog offers to save. */
  private editorRef = React.createRef<ScheduledJobEditor>();

  /** Set once this canvas has unmounted; a `loadJobs` still in flight then must not `setState`. */
  private isUnmounted: boolean = false;

  /**
   * The update's state machine: the ✓ is held on UPDATE before anything else happens, so an
   * "Update & close" shows its result before the editor leaves. It is the datasource editors'
   * machine because this is the same kind of surface — an editor with a header save — and its
   * `canStart` keeps a second submit out while the ✓ is still up.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /**
   * The create's state machine, shown on the name sheet's confirm. A create opens the new job's
   * editor, which replaces the list and the sheet with it — so the ✓ is held first and the editor
   * opens after it, and `canStart` keeps an Enter pressed during the ✓ from creating a second job.
   */
  private readonly createStatus: SaveStatus = new SaveStatus((createStatus: KuikaButtonStatus): void => {
    this.setState({ createStatus });
  });

  constructor(props: IScheduledJobsCanvasProps & IWithSDProps) {
    super(props);
    this.state = {
      isLoading: false,
      saveStatus: "idle",
      createStatus: "idle",
      isListLoading: true,
      busyJobId: undefined,
      jobToDelete: undefined
    };
  }

  componentDidMount = (): void => {
    void this.loadJobs();
  };

  loadJobs = async (): Promise<void> => {
    try {
      await this.props.sd.loadScheduledJobs(this.props.sd?.project?.id);
    } finally {
      // A bare `return` here would be an unsafe finally (it can swallow the try's rejection),
      // so the unmount guard wraps the setState instead of short-circuiting the block.
      if (!this.isUnmounted) {
        this.setState({ isListLoading: false });
      }
    }
  };

  componentWillUnmount(): void {
    this.isUnmounted = true;
    this.saveStatus.dispose();
    this.createStatus.dispose();
  }

  componentDidUpdate(): void {
    // A pending cross-tool navigation asks the editor to close first — but not while an update
    // is running or holding its ✓: that update closes the editor itself once the ✓ has been seen
    // (see `handleUpdate`), and asking now would close it under the button.
    if (this.props.nextCallback && this.getOpenJob() && this.saveStatus.canStart && !this.isClosingAfterSave) {
      this.handleRequestClose();
    }
  }

  getOpenJob = (): IScheduledJob | undefined => {
    if (this.props.sd.isCreateSJobActive) return undefined;
    return this.props.sd.getSelectedSj();
  };

  /** Sorted, because the switcher and the landing table must agree on the order. */
  getJobs = (): IScheduledJob[] => sortScheduledJobs(this.props.sd?.scheduledJobs ?? []);

  //  ── Dirty tracking ─────────────────────────────────────────────────────────
  //  The editor reports its live form state; the baseline is re-taken whenever a job
  //  opens and whenever a save lands. `actions` are deliberately NOT compared: the right
  //  panel persists them the moment they are attached, so including them made an
  //  already-saved job read as dirty.

  registerDirtyProbe = (probe: () => IScheduledJobEditorSnapshot): void => {
    this.readEditor = probe;
    this.baseline = probe();
  };

  isDirty = (): boolean => {
    if (!this.readEditor || !this.baseline) return false;
    const current: IScheduledJobEditorSnapshot = this.readEditor();
    return (
      current.name !== this.baseline.name ||
      current.cronValue !== this.baseline.cronValue ||
      current.timeZone !== this.baseline.timeZone
    );
  };

  //  ── Mutations ──────────────────────────────────────────────────────────────

  createJob = async (name: string, cronValue: string, timeZone: string): Promise<void> => {
    if (!this.createStatus.canStart) return;
    this.createStatus.start();
    this.setState({ isLoading: true });
    try {
      const job: IScheduledJobsDto = {
        id: Guid.create().toString(),
        name,
        kProject_Id: this.props.sd.project?.id,
        json: JSON.stringify({ cronValue, timeZone, isActive: true }),
        xml: "scheduledjobs"
      } as IScheduledJobsDto;
      // `saveScheduledJob` reports its own failure and resolves `false`; on success it resolves
      // once the list holds the new job, which is what the editor opens on.
      const isCreated: boolean = await this.props.sd.saveScheduledJob(job);
      if (!isCreated) {
        this.createStatus.fail();
        return;
      }
      this.createStatus.succeed((): void => {
        this.props.sd.setCreateSJobActive(false);
        this.props.sd.setActiveScheduledJob(job.id);
      });
    } catch (error) {
      this.createStatus.fail();
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleCreateBlank = (name: string): Promise<void> => this.createJob(name, DEFAULT_CRON_VALUE, DEFAULT_TIME_ZONE);

  handleCreateFromTemplate = (template: IScheduledJobTemplate, name: string): Promise<void> =>
    this.createJob(name, template.cronValue, template.timeZone);

  handleUpdate = async (values: IScheduledJobEditorFormValues, cronValue: string): Promise<void> => {
    const openJob: IScheduledJob | undefined = this.getOpenJob();
    if (!openJob || !this.saveStatus.canStart) return;

    this.saveStatus.start();
    this.setState({ isLoading: true });
    try {
      const json = typeof openJob.json === "string" ? JSON.parse(openJob.json) : { ...(openJob.json ?? {}) };
      json.cronValue = cronValue;
      json.timeZone = values.timeZone;

      const payload: IScheduledJobsDto = {
        id: openJob.id,
        name: values.name,
        kProject_Id: this.props.sd.project?.id,
        json: JSON.stringify(json),
        xml: "scheduledjobs"
      } as IScheduledJobsDto;

      await this.props.sd.saveScheduledJob(payload);
      // Re-baseline from what was SUBMITTED. The old code rebuilt it from the pre-save
      // job, so a rename left the previous name in the baseline and the editor announced
      // unsaved changes on a job that had just been saved.
      this.baseline = { name: values.name, cronValue, timeZone: values.timeZone };
      this.props.setShowModal(false);
      // The editor stays open after a plain UPDATE, so the ✓ on the button is where the save
      // is confirmed. An "Update & close" closes only after that ✓ has been held.
      this.isClosingAfterSave = this.pendingClose;
      this.pendingClose = false;
      this.saveStatus.succeed((): void => {
        if (!this.isClosingAfterSave) return;
        this.isClosingAfterSave = false;
        this.closeEditor();
      });
    } catch (error) {
      // The editor stays open with the button saying "Try again". A failed "Update & close"
      // must not leave its close armed for the next, plain UPDATE.
      this.pendingClose = false;
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleToggleActive = async (job: IScheduledJob): Promise<void> => {
    const isActive: boolean = !!job.json?.isActive;
    this.setState({ busyJobId: job.id });
    try {
      if (isActive) {
        await ScheduledJobsService.Inactivate(job.id);
      } else {
        await ScheduledJobsService.Activate(job.id);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    await this.props.sd.loadScheduledJobs(this.props.sd.project.id);
    this.setState({ busyJobId: undefined });
  };

  /**
   * The panel's delete sequence, kept verbatim — service, then prune the StartProcess
   * reverse index, then reload, then fall back to the list if the deleted job was open.
   * The provider's own `deleteScheduledJob` is NOT used: it re-saves the job it has just
   * deleted.
   */
  handleDelete = async (job: IScheduledJob): Promise<void> => {
    this.setState({ busyJobId: job.id });
    try {
      const response = await ScheduledJobsService.Delete(job.id);
      if (response) {
        getSdInstance().startProcessTriggerSync.pruneScheduledJob(job.id);
        await this.props.sd.loadScheduledJobs(this.props.sd.project.id);
        if (this.props.sd.selectedSjId === job.id) {
          this.props.sd.setCreateSJobActive(true);
        }
        KMainFunctions.displaySuccessNotification("Scheduled Job deleted successfully");
      }
    } catch (error) {
      KMainFunctions.displayErrorNotification("An error occurred!", error);
    }
    this.setState({ busyJobId: undefined, jobToDelete: undefined });
  };

  //  ── Navigation ─────────────────────────────────────────────────────────────

  handleOpenJob = async (job: IScheduledJob): Promise<void> => {
    await openScheduledJobForEditing(getSdInstance(), job.id, { ensureView: false });
  };

  /** Selecting from the editor's switcher goes through the same dirty handshake. */
  handleSwitchJob = (job: IScheduledJob): void => {
    if (!this.isDirty()) {
      void this.handleOpenJob(job);
      return;
    }
    this.props.setNextCallbackFunction(() => {
      void this.handleOpenJob(job);
    });
    this.props.setShowModal(true);
  };

  handleCreateNewFromEditor = (): void => {
    if (!this.isDirty()) {
      this.closeEditor();
      return;
    }
    this.props.setNextCallbackFunction(() => this.closeEditor());
    this.props.setShowModal(true);
  };

  handleRequestClose = (): void => {
    if (!this.isDirty()) {
      this.closeEditor();
      return;
    }
    this.props.setShowModal(true);
  };

  closeEditor = (): void => {
    const next: (() => void) | undefined = this.props.nextCallback;
    this.baseline = undefined;
    this.readEditor = undefined;
    this.props.sd.cancelEditingScheduledJob();
    this.props.sd.setCreateSJobActive(true);
    this.props.setShowModal(false);
    if (next) {
      next();
      this.props.setNextCallbackFunction();
    }
  };

  render() {
    if (!this.props.sd || this.props.sd.dashboardState !== DashboardState.scheduledJobs) {
      return null;
    }

    const openJob: IScheduledJob | undefined = this.getOpenJob();

    return (
      <>
        <CloseConfirmationModal
          itemName={openJob?.name ?? ""}
          closeType={"Scheduled Job"}
          triggerModal={this.props.showModal}
          isLoading={this.state.isLoading}
          updateAndCloseHandler={() => {
            this.pendingClose = true;
            this.editorRef.current?.formRef.current?.submit();
          }}
          closeWithoutSaveHandler={this.closeEditor}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />

        <KuikaSpinnerSmall spinning={this.state.isLoading && !openJob}>
          {openJob ? (
            <ScheduledJobEditor
              ref={this.editorRef}
              key={openJob.id}
              job={openJob}
              jobs={this.getJobs()}
              saveStatus={this.state.saveStatus}
              onUpdate={this.handleUpdate}
              onSwitchJob={this.handleSwitchJob}
              onCreateNew={this.handleCreateNewFromEditor}
              onRequestClose={this.handleRequestClose}
              registerDirtyProbe={this.registerDirtyProbe}
              rowActions={buildScheduledJobRowActions({
                onToggleActive: (job: IScheduledJob) => void this.handleToggleActive(job),
                onRequestDelete: (job: IScheduledJob) => this.setState({ jobToDelete: job })
              })}
            />
          ) : (
            <ScheduledJobsLanding
              isCreating={this.state.isLoading}
              createStatus={this.state.createStatus}
              isListLoading={this.state.isListLoading}
              busyJobId={this.state.busyJobId}
              onCreateBlank={this.handleCreateBlank}
              onCreateFromTemplate={this.handleCreateFromTemplate}
              onOpenJob={(job: IScheduledJob) => void this.handleOpenJob(job)}
              onToggleActive={(job: IScheduledJob) => void this.handleToggleActive(job)}
              onRequestDelete={(job: IScheduledJob) => this.setState({ jobToDelete: job })}
            />
          )}
        </KuikaSpinnerSmall>

        {/* Owned by the host so the confirmation survives the surface it was opened from —
            the editor closes itself when the open job is the one deleted. */}
        <DeleteModal
          itemName={this.state.jobToDelete?.name ?? ""}
          triggerModal={!!this.state.jobToDelete}
          deleteItem={() => {
            const target: IScheduledJob | undefined = this.state.jobToDelete;
            if (target) void this.handleDelete(target);
          }}
          deleteType={"Scheduled Job"}
          closeModal={() => this.setState({ jobToDelete: undefined })}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const scheduledJobsCanvas = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<IScheduledJobsCanvasProps>(ScheduledJobsCanvas));
export { scheduledJobsCanvas as ScheduledJobsCanvas };
